<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import * as z from 'zod';
import { useForm } from 'vee-validate';
import { onMounted, onUnmounted, ref } from 'vue';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/form';
import { Button } from '../../components/ui/button';
import { Info } from 'lucide-vue-next';
import { registerAction } from '@/actions/auth/RegisterAction.ts';
import { focusRefElement, handleFormError } from '@/utils/RandomUtils.ts';
import { ROUTES } from '@/router.ts';
import Loader from '@/components/loader/Loader.vue';
import { Checkbox } from '@/components/ui/checkbox';

type Turnstile = {
    render: (
        container: HTMLElement,
        options: {
            sitekey: string;
            theme: 'auto' | 'light' | 'dark';
            callback: (token: string) => void;
            'expired-callback'?: () => void;
            'error-callback'?: () => void;
        },
    ) => string;
    reset: (widgetId?: string) => void;
    remove: (widgetId?: string) => void;
};

type TurnstileWindow = Window &
    typeof globalThis & {
        turnstile?: Turnstile;
    };

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
const isCaptchaEnabled = Boolean(turnstileSiteKey);

const usernameInput = ref<typeof Input | null>(null);
const captchaContainer = ref<HTMLDivElement | null>(null);
const captchaWidgetId = ref<string>();
const captchaToken = ref('');
const captchaError = ref<string | null>(null);

const isLoading = ref(false);

const termsAccepted = ref(false);
const termsHighlighted = ref(false);

let turnstileScriptPromise: Promise<Turnstile> | null = null;

const formSchema = toTypedSchema(
    z.object({
        _: z.unknown(),
        username: z.string().regex(/^\S*$/, 'Username should not contain spaces').min(2).max(50),
        password: z.string().min(8).max(100),
        referralSource: z.string().optional().or(z.literal('')),
    }),
);

const form = useForm({
    validationSchema: formSchema,
});

const getTurnstile = (): Turnstile | undefined => {
    return (window as TurnstileWindow).turnstile;
};

const loadTurnstileScript = async (): Promise<Turnstile> => {
    const turnstile = getTurnstile();
    if (turnstile) {
        return turnstile;
    }

    if (!turnstileScriptPromise) {
        turnstileScriptPromise = new Promise<Turnstile>((resolve, reject) => {
            const existingScript = document.getElementById(
                TURNSTILE_SCRIPT_ID,
            ) as HTMLScriptElement | null;
            const script = existingScript ?? document.createElement('script');

            const cleanup = () => {
                script.removeEventListener('load', handleLoad);
                script.removeEventListener('error', handleError);
            };

            const handleLoad = () => {
                cleanup();

                const loadedTurnstile = getTurnstile();
                if (!loadedTurnstile) {
                    turnstileScriptPromise = null;
                    reject(new Error('Turnstile failed to initialize.'));

                    return;
                }

                resolve(loadedTurnstile);
            };

            const handleError = () => {
                cleanup();
                turnstileScriptPromise = null;
                reject(new Error('Turnstile script failed to load.'));
            };

            script.addEventListener('load', handleLoad, { once: true });
            script.addEventListener('error', handleError, { once: true });

            if (!existingScript) {
                script.id = TURNSTILE_SCRIPT_ID;
                script.src =
                    'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
                script.async = true;
                script.defer = true;
                document.head.appendChild(script);
            }
        });
    }

    return turnstileScriptPromise;
};

const resetCaptcha = () => {
    captchaToken.value = '';

    const turnstile = getTurnstile();
    if (turnstile && captchaWidgetId.value) {
        turnstile.reset(captchaWidgetId.value);
    }
};

const initializeCaptcha = async (): Promise<void> => {
    if (!isCaptchaEnabled || !captchaContainer.value || captchaWidgetId.value) {
        return;
    }

    try {
        const turnstile = await loadTurnstileScript();

        captchaWidgetId.value = turnstile.render(captchaContainer.value, {
            sitekey: turnstileSiteKey,
            theme: 'auto',
            callback: (token: string) => {
                captchaToken.value = token;
                captchaError.value = null;
            },
            'expired-callback': () => {
                captchaToken.value = '';
                captchaError.value = 'The CAPTCHA challenge expired. Please try again.';
            },
            'error-callback': () => {
                captchaToken.value = '';
                captchaError.value =
                    'The CAPTCHA challenge failed to load. Please refresh and try again.';
            },
        });
    } catch {
        captchaError.value = 'The CAPTCHA challenge failed to load. Please refresh and try again.';
    }
};

const onSubmit = form.handleSubmit(async (values): Promise<void> => {
    isLoading.value = true;

    if (values.username.includes('@')) {
        form.setErrors({
            username:
                'To protect your privacy, please do not use an email address as your username.',
        });

        isLoading.value = false;

        return;
    }

    if (!termsAccepted.value) {
        form.setErrors({
            _: 'You must accept our terms to register.',
        });

        isLoading.value = false;
        termsHighlighted.value = true;

        return;
    }

    if (isCaptchaEnabled && !captchaToken.value) {
        form.setErrors({
            _: captchaError.value ?? 'Please complete the CAPTCHA challenge.',
        });

        isLoading.value = false;

        return;
    }

    try {
        await registerAction(
            values.username,
            values.password,
            captchaToken.value,
            values.referralSource,
        );
    } catch (error) {
        resetCaptcha();
        isLoading.value = false;

        await handleFormError(form, error, false);

        return;
    }

    isLoading.value = false;
});

onMounted(() => {
    focusRefElement(usernameInput);
    void initializeCaptcha();
});

onUnmounted(() => {
    const turnstile = getTurnstile();
    if (turnstile && captchaWidgetId.value) {
        turnstile.remove(captchaWidgetId.value);
        captchaWidgetId.value = undefined;
    }
});
</script>

<template>
    <section class="mx-auto flex h-full flex-col items-center justify-center">
        <h1 class="mb-6 flex items-center text-2xl font-semibold text-card-foreground">
            🧙‍♂️ Leetcode Wizard
        </h1>
        <Card class="w-full max-w-sm md:mt-0 xl:p-0">
            <CardHeader>
                <CardTitle class="text-xl">Register</CardTitle>
            </CardHeader>

            <CardContent class="pb-3">
                <form @submit="onSubmit">
                    <div v-if="form.errorBag.value._">
                        <ul class="mb-4">
                            <li
                                v-for="(error, index) in form.errorBag.value._"
                                :key="index"
                                class="text-sm font-medium text-red-700"
                            >
                                {{ error }}
                            </li>
                        </ul>
                    </div>

                    <FormField
                        v-slot="{ componentField, errors }"
                        name="username"
                        :validate-on-input="false"
                        :validate-on-model-update="false"
                        :validate-on-blur="false"
                    >
                        <FormItem class="mb-4">
                            <FormLabel>Username</FormLabel>
                            <FormControl>
                                <Input
                                    ref="usernameInput"
                                    type="text"
                                    v-bind="componentField"
                                    :disabled="isLoading"
                                    autocomplete="username"
                                    class="h-8"
                                />
                            </FormControl>
                            <FormMessage v-if="errors">
                                {{ errors }}
                            </FormMessage>
                        </FormItem>
                    </FormField>

                    <FormField
                        v-slot="{ componentField, errors }"
                        name="password"
                        :validate-on-input="false"
                        :validate-on-model-update="false"
                        :validate-on-blur="false"
                    >
                        <FormItem class="mb-4">
                            <FormLabel>Password</FormLabel>
                            <FormControl>
                                <Input
                                    type="password"
                                    v-bind="componentField"
                                    :disabled="isLoading"
                                    autocomplete="new-password"
                                    class="h-8"
                                />
                            </FormControl>
                            <FormMessage v-if="errors">
                                {{ errors }}
                            </FormMessage>
                        </FormItem>
                    </FormField>

                    <FormField
                        v-slot="{ componentField, errors }"
                        name="referralSource"
                        :validate-on-input="false"
                        :validate-on-model-update="false"
                        :validate-on-blur="false"
                    >
                        <FormItem class="mb-4">
                            <FormLabel>Where did you first hear about us? (optional)</FormLabel>
                            <FormControl>
                                <Input
                                    type="text"
                                    v-bind="componentField"
                                    :disabled="isLoading"
                                    class="h-8"
                                />
                            </FormControl>
                            <FormMessage v-if="errors">
                                {{ errors }}
                            </FormMessage>
                        </FormItem>
                    </FormField>

                    <div class="items-top mb-4 flex gap-x-2">
                        <Checkbox id="terms" v-model="termsAccepted" :disabled="isLoading" />
                        <div class="grid gap-1.5 leading-none">
                            <label
                                for="terms"
                                class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                :class="{ 'text-red-700': termsHighlighted && !termsAccepted }"
                            >
                                Accept Terms
                            </label>
                            <p class="text-xs text-muted-foreground">
                                You agree to our
                                <a
                                    href="https://leetcodewizard.io/legal/terms-and-conditions"
                                    class="text-blue-500 hover:underline"
                                    target="_blank"
                                    >Terms and Conditions</a
                                >,
                                <a
                                    href="https://leetcodewizard.io/legal/privacy-policy"
                                    class="text-blue-500 hover:underline"
                                    target="_blank"
                                    >Privacy Policy</a
                                >
                                and
                                <a
                                    href="https://leetcodewizard.io/legal/refund-policy"
                                    class="text-blue-500 hover:underline"
                                    target="_blank"
                                    >Refund Policy</a
                                >.
                            </p>
                        </div>
                    </div>

                    <div v-if="isCaptchaEnabled" class="mb-4">
                        <div ref="captchaContainer" class="min-h-[65px]" />
                        <p v-if="captchaError" class="mt-2 text-sm font-medium text-red-700">
                            {{ captchaError }}
                        </p>
                    </div>

                    <Button
                        type="submit"
                        :disabled="isLoading"
                        class="flex w-full items-center justify-center"
                    >
                        <span v-if="!isLoading">Submit</span>
                        <Loader v-else :width="18" :height="18" />
                    </Button>
                </form>
            </CardContent>
            <CardFooter class="flex flex-row">
                <p class="w-full text-center text-sm">
                    Already have an account?
                    <RouterLink :to="ROUTES.authLogin" class="font-medium hover:underline">
                        Log in here
                    </RouterLink>
                </p>
            </CardFooter>
        </Card>

        <div
            class="mt-4 flex w-full max-w-sm items-center rounded-lg border border-blue-300 bg-card p-4 text-xs text-blue-400 dark:border-blue-800"
            role="alert"
        >
            <Info class="me-3 inline h-4 w-4 flex-shrink-0" />
            <span class="sr-only">Info</span>
            <div>
                The privacy of our users is our highest priority. We do not collect email addresses.
                If you forget your password, please create a new account or contact our support
                team.
            </div>
        </div>
    </section>
</template>
